/**
 * VAT totals aligned with productionTpoBack printDocumentController (albarán footer).
 * Per line: round(gross) → round(gross/(1+IVA%)) base → bands → round(band×rate%) tax.
 */

const roundToDecimals = (value, decimals = 2) => {
  const n = Number(value)
  if (!Number.isFinite(n)) return 0
  return parseFloat(n.toFixed(decimals))
}

const safeParse = (value, defaultValue = 0) => {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (value === null || value === undefined || value === '') return defaultValue
  const parsed = parseFloat(String(value).trim().replace(',', '.'))
  return Number.isFinite(parsed) ? parsed : defaultValue
}

/** Same as printDocumentController / factura PDF (uses IVA, not IVACP). */
export const getItemIvaRate = (item) => {
  const raw = item?.IVA
  if (raw === undefined || raw === null || raw === '') return 0
  const n = Number(raw)
  return Number.isFinite(n) ? n : 0
}

/** Line gross = round(round(price, 2) × round(qty, 2), 2). */
export const getLineTotalGross = (item) => {
  const raw = safeParse(
    item?.marketPriceCP ?? item?.marketPrice ?? item?.price,
    0,
  )
  const quantity = safeParse(item?.quantity, 1)
  const unitPrice = roundToDecimals(raw, 2)
  const roundedQty = roundToDecimals(quantity, 2)
  return roundToDecimals(unitPrice * roundedQty, 2)
}

/** Base imponible per line (con-IVA gross → net). */
export const getLineBaseImponible = (item) => {
  const rate = getItemIvaRate(item)
  const lineGross = getLineTotalGross(item)
  if (rate === 0) return lineGross
  return roundToDecimals(lineGross / (1 + rate / 100), 2)
}

export const getUniqueIvaRatesFromItems = (items = []) => {
  const rates = new Set()
  for (const item of items) {
    rates.add(getItemIvaRate(item))
  }
  return [...rates].sort((a, b) => a - b)
}

/** One IVA band: base = Σ line bases; tax = round(base × rate%, 2). */
export const computeIvaBandSummary = (items, ivaRate) => {
  const rate = Number(ivaRate) || 0
  const bandItems = (items || []).filter(
    (item) => getItemIvaRate(item) === rate,
  )
  if (!bandItems.length) return null

  const baseSum = bandItems.reduce(
    (sum, item) => sum + getLineBaseImponible(item),
    0,
  )
  const base = roundToDecimals(baseSum, 2)
  const tax = roundToDecimals((base * rate) / 100, 2)
  const gross = roundToDecimals(base + tax, 2)

  return { base, tax, gross, rate }
}

export const getItemsIncludingDelivery = (
  order,
  deliveryIvaRate = 10,
) => {
  const items = [...(order?.items || [])]
  const deliveryGross = roundToDecimals(
    safeParse(order?.deliveryPrice, 0),
    2,
  )
  if (deliveryGross > 0) {
    items.push({
      marketPriceCP: deliveryGross,
      quantity: 1,
      IVA: deliveryIvaRate,
    })
  }
  return items
}

/**
 * Order VAT breakdown (matches albarán BASE / TOTAL IMPUESTO / gross).
 */
export const computeOrderVatBreakdown = (
  order,
  { includeDelivery = true, deliveryIvaRate = 10 } = {},
) => {
  const items = includeDelivery
    ? getItemsIncludingDelivery(order, deliveryIvaRate)
    : order?.items || []

  let base = 0
  let tax = 0
  let gross = 0

  for (const rate of getUniqueIvaRatesFromItems(items)) {
    const band = computeIvaBandSummary(items, rate)
    if (!band) continue
    base += band.base
    tax += band.tax
    gross += band.gross
  }

  return {
    base: roundToDecimals(base, 2),
    tax: roundToDecimals(tax, 2),
    gross: roundToDecimals(gross, 2),
  }
}
